import { DIST_EPSILON } from "./core";
import { polyLine2ConvexHullVertexIndices, polyLine2Vertex, polyLine2VertexCount } from "./polyLine2";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import { type Vec2, vec2Dot, vec2Sub } from "./vec2";

// Uses the rotating calipers algorithm to find the minimum bounding box of a polyline in O(n) time.
// https://www.geometrictools.com/Documentation/MinimumAreaRectangle.pdf
// Variable names are chosen to match the paper.

// Because there are so many different index domains and scaling, we use nominal types to distinguish them
// and increase readability.
// Numbers that are not NominalNumbers are in world scale.
type NominalNumberBase<T extends NominalNumberBasis> = number & { [K in T]: never };

declare const PolylineIndexSymbol: unique symbol;
declare const HullIndexSymbol: unique symbol;
declare const SupportIndexSymbol: unique symbol;
declare const ScaledByW0LenSymbol: unique symbol;
declare const ScaledByW0LenSqrSymbol: unique symbol;

type PolylineIndexBasis = typeof PolylineIndexSymbol;
type HullIndexBasis = typeof HullIndexSymbol;
type SupportIndexBasis = typeof SupportIndexSymbol;
type ScaledByW0LenBasis = typeof ScaledByW0LenSymbol;
type ScaledByW0LenSqrBasis = typeof ScaledByW0LenSqrSymbol;

type NominalNumberBasis =
  | PolylineIndexBasis
  | HullIndexBasis
  | SupportIndexBasis
  | ScaledByW0LenBasis
  | ScaledByW0LenSqrBasis;
type PolylineIndex = NominalNumberBase<PolylineIndexBasis>;
type HullIndex = NominalNumberBase<HullIndexBasis>;
type SupportIndex = NominalNumberBase<SupportIndexBasis>;
type ScaledByW0Len = NominalNumberBase<ScaledByW0LenBasis>; // In caliper W0, W1 coordinates (see paper).
type ScaledByW0LenSqr = NominalNumberBase<ScaledByW0LenSqrBasis>;

type IndexType = PolylineIndex | HullIndex | SupportIndex;

// A bounding rectangle that is not necessarily axis-aligned.
// One corner is at (x, y) and the sides are defined by u and v.
export interface BoundingRect {
  x: number;
  y: number;
  u: Vec2; // not normalized, the full length of the rectangle side.
  v: Vec2; // not normalized, the full length of the rectangle side.
}

interface SupportVertex {
  vertexIndex: HullIndex; // vertex index in the hull.
  supportVertex: ScaledByW0Len[]; // the support vertex in caliper coordinates.

  // Angle proxies are (u0len^2)*sin(angle)^2. See paper for why ordering by this proxy is the same as ordering by angle.
  angleProxy: number | null; // The angle proxy for the edge following this support vertex.
  edgeVector: number[]; // The edge vector for the edge following this support vertex.
}

interface RotatingCaliper {
  // w0 is the caliper's horizontal axis, w1 is the caliper's vertical axis (not normalized). u0, u1 would be the normalized axes.
  w0x: number;
  w0y: number;
  w1x: number;
  w1y: number;
  bottomIndex: SupportIndex; // Index into the three arrays below, for the bottom support vertex.
  supports: SupportVertex[]; // Order: bottom, right, top, left, starting at `bottomIndex` and wrapping around.
}

function hullVertex(polyline: PolyLine2, hullIndices: PolylineIndex[], i: number) {
  return polyLine2Vertex(polyline, hullIndices[i % hullIndices.length]);
}

function nextIndex<T extends IndexType>(index: T, modulo: number): T {
  return ((index + 1) % modulo) as T;
}

function offsetIndexBy<T extends IndexType>(index: T, offset: number, modulo: number): T {
  return ((index + offset) % modulo) as T;
}

function calculateMinAngleProxy(
  caliper: RotatingCaliper,
  polyline: PolyLine2,
  convexHullVertexIndices: PolylineIndex[],
) {
  const l = convexHullVertexIndices.length;
  const supportedEdgeVectors: number[][] = [
    [caliper.w0x, caliper.w0y],
    [caliper.w1x, caliper.w1y],
    [-caliper.w0x, -caliper.w0y],
    [-caliper.w1x, -caliper.w1y],
  ];
  let minAngleProxy = Number.MAX_VALUE;

  // Calculate after-angles for the four supports.
  for (let j = 0; j < 4; ++j) {
    const indexFromBottom = offsetIndexBy(caliper.bottomIndex, j, 4);
    if (caliper.supports[indexFromBottom].vertexIndex === caliper.supports[nextIndex(indexFromBottom, 4)].vertexIndex) {
      // Support is coincident with the next support.
      caliper.supports[indexFromBottom].angleProxy = null;
      continue;
    }
    const supportIndex = caliper.supports[indexFromBottom].vertexIndex;
    const followingIndex = nextIndex(supportIndex, l);
    const [startX, startY] = hullVertex(polyline, convexHullVertexIndices, supportIndex);
    const [endX, endY] = hullVertex(polyline, convexHullVertexIndices, followingIndex);
    const wix = endX - startX;
    const wiy = endY - startY;
    const angleProxy = calculateAngleProxy(supportedEdgeVectors[j][0], supportedEdgeVectors[j][1], wix, wiy);
    caliper.supports[indexFromBottom].edgeVector = [wix, wiy];
    caliper.supports[indexFromBottom].angleProxy = angleProxy;
    if (angleProxy < minAngleProxy) {
      minAngleProxy = angleProxy;
    }
  }
  return minAngleProxy;
}

// See formula (6) in the paper linked in the description of `polyLine2MinBoundingRect`.
function calculateAngleProxy(wx: number, wy: number, ex: number, ey: number) {
  const wDotPerpE = vec2Dot(wx, wy, ey, -ex);
  const numerator = wDotPerpE * wDotPerpE;
  const eLenSqr = ex * ex + ey * ey;
  return numerator / eLenSqr;
}

function transformToCaliperCoords(
  caliper: RotatingCaliper,
  hullIndex: HullIndex,
  polyline: PolyLine2,
  hullIndices: PolylineIndex[],
): [ScaledByW0Len, ScaledByW0Len] {
  const [vX, vY] = hullVertex(polyline, hullIndices, hullIndex);
  const [originX, originY] = hullVertex(polyline, hullIndices, caliper.supports[caliper.bottomIndex].vertexIndex);
  const diff = vec2Sub(vX, vY, originX, originY);
  const caliperVx = vec2Dot(caliper.w0x, caliper.w0y, diff[0], diff[1]) as ScaledByW0Len;
  const caliperVy = vec2Dot(caliper.w1x, caliper.w1y, diff[0], diff[1]) as ScaledByW0Len;
  return [caliperVx, caliperVy];
}

function initializeCaliper(polyline: PolyLine2, hullIndices: PolylineIndex[]): RotatingCaliper {
  const l = hullIndices.length;
  const [startX, startY] = hullVertex(polyline, hullIndices, l - 1);
  const [endX, endY] = hullVertex(polyline, hullIndices, 0);
  const w0 = vec2Sub(endX, endY, startX, startY);

  const caliper: RotatingCaliper = {
    w0x: w0[0],
    w0y: w0[1],
    w1x: -w0[1],
    w1y: w0[0],
    bottomIndex: 0 as SupportIndex,
    supports: [0, 1, 2, 3].map(() => {
      return {
        vertexIndex: 0 as HullIndex,
        supportVertex: [0 as ScaledByW0Len, 0 as ScaledByW0Len],
        angleProxy: null,
        edgeVector: [0, 0],
      };
    }),
  };

  const supports = caliper.supports;
  for (let i: HullIndex = 1 as HullIndex; i < l; ++i) {
    const [caliperVix, caliperViy] = transformToCaliperCoords(caliper, i, polyline, hullIndices);
    if (
      caliperVix > supports[1].supportVertex[0] ||
      (caliperVix === supports[1].supportVertex[0] && caliperViy > supports[1].supportVertex[1])
    ) {
      caliper.supports[1].vertexIndex = i;
      supports[1].supportVertex[0] = caliperVix;
      supports[1].supportVertex[1] = caliperViy;
    }
    if (
      caliperViy > supports[2].supportVertex[1] ||
      (caliperViy === supports[2].supportVertex[1] && caliperVix < supports[2].supportVertex[0])
    ) {
      caliper.supports[2].vertexIndex = i;
      supports[2].supportVertex[0] = caliperVix;
      supports[2].supportVertex[1] = caliperViy;
    }
    if (
      caliperVix < supports[3].supportVertex[0] ||
      (caliperVix === supports[3].supportVertex[0] && caliperViy < supports[3].supportVertex[1])
    ) {
      caliper.supports[3].vertexIndex = i;
      supports[3].supportVertex[0] = caliperVix;
      supports[3].supportVertex[1] = caliperViy;
    }
  }

  return caliper;
}

function bottomVertexInCaliperCoords(caliper: RotatingCaliper) {
  return caliper.supports[caliper.bottomIndex].supportVertex;
}

function rightVertexInCaliperCoords(caliper: RotatingCaliper) {
  return caliper.supports[(caliper.bottomIndex + 1) % 4].supportVertex;
}

function topVertexInCaliperCoords(caliper: RotatingCaliper) {
  return caliper.supports[(caliper.bottomIndex + 2) % 4].supportVertex;
}

function leftVertexInCaliperCoords(caliper: RotatingCaliper) {
  return caliper.supports[(caliper.bottomIndex + 3) % 4].supportVertex;
}

function calculateCaliperArea(caliper: RotatingCaliper, polyline: PolyLine2, hullIndices: PolylineIndex[]) {
  const bottom = bottomVertexInCaliperCoords(caliper);
  const right = rightVertexInCaliperCoords(caliper);
  const top = topVertexInCaliperCoords(caliper);
  const left = leftVertexInCaliperCoords(caliper);

  const origin = hullVertex(polyline, hullIndices, caliper.supports[caliper.bottomIndex].vertexIndex);
  const originX = origin[0];
  const originY = origin[1];

  const caliperWidth = (right[0] - left[0]) as ScaledByW0Len;
  const caliperHeight = (top[1] - bottom[1]) as ScaledByW0Len;
  const w0lenSqr = (caliper.w0x * caliper.w0x + caliper.w0y * caliper.w0y) as ScaledByW0LenSqr;
  const area = (caliperWidth * caliperHeight) / w0lenSqr;

  // Variables below are not in caliper coordinates.
  const bottomLeftX = originX + (left[0] * caliper.w0x) / w0lenSqr; // bottom is 0 in caliper coordinates.
  const bottomLeftY = originY + (left[0] * caliper.w0y) / w0lenSqr; // bottom is 0 in caliper coordinates.
  const bottomRightX = originX + (right[0] * caliper.w0x) / w0lenSqr; // bottom is 0 in caliper coordinates.
  const bottomRightY = originY + (right[0] * caliper.w0y) / w0lenSqr; // bottom is 0 in caliper coordinates.
  const topLeftX = originX + (left[0] * caliper.w0x + top[1] * caliper.w1x) / w0lenSqr;
  const topLeftY = originY + (left[0] * caliper.w0y + top[1] * caliper.w1y) / w0lenSqr;
  return { area, bottomLeftX, bottomLeftY, bottomRightX, bottomRightY, topLeftX, topLeftY };
}

export function polyLine2MinBoundingRect(polyline: PolyLine2): BoundingRect | null {
  if (polyLine2VertexCount(polyline) <= 3) {
    // TODO: Handle degenerate cases.
    return null;
  }

  const convexHullVertexIndices: PolylineIndex[] = polyLine2ConvexHullVertexIndices(polyline, DIST_EPSILON)
    .reverse()
    .map((x) => x as PolylineIndex);
  if (convexHullVertexIndices.length < 3) {
    // TODO: Handle degenerate cases.
    return null;
  }

  // `polyLine2ConvexHullVertexIndices` removed coincident and colinear vertices (within DIST_EPSILON),
  // so the convex hull is a simple polygon which is important for the rotating calipers algorithm.

  // The convex hull does NOT repeat the first vertex at the end of the index array,
  // the calculations are done in modulo `l` following the paper.
  const l = convexHullVertexIndices.length;

  const caliper = initializeCaliper(polyline, convexHullVertexIndices);
  const { area, bottomLeftX, bottomLeftY, bottomRightX, bottomRightY, topLeftX, topLeftY } = calculateCaliperArea(
    caliper,
    polyline,
    convexHullVertexIndices,
  );
  let minAreaRect: BoundingRect = {
    x: bottomLeftX,
    y: bottomLeftY,
    u: [bottomRightX - bottomLeftX, bottomRightY - bottomLeftY],
    v: [topLeftX - bottomLeftX, topLeftY - bottomLeftY],
  };
  let minArea = area;

  for (let i = 0; i < l; ++i) {
    const minAngleProxy = calculateMinAngleProxy(caliper, polyline, convexHullVertexIndices);

    // Index of the first minimum angle support starting from `bottomIndex` and wrapping around.
    // This will be the new bottom support.
    const bottomOffset = caliper.bottomIndex;
    let first = true;
    for (let j = 0; j < 4; ++j) {
      const indexFromBottom = offsetIndexBy(bottomOffset, j, 4);
      const angleProxy = caliper.supports[indexFromBottom].angleProxy;
      if (angleProxy === null) {
        // First of a pair of coincident supports.
        continue;
      }

      if (Math.abs(angleProxy - minAngleProxy) < DIST_EPSILON) {
        const next = nextIndex(caliper.supports[indexFromBottom].vertexIndex, l);
        // The bottom index WILL change. If it hasn't changed yet this is the first minimum angle support.
        if (first) {
          first = false;
          caliper.bottomIndex = indexFromBottom;
          caliper.w0x = caliper.supports[indexFromBottom].edgeVector[0];
          caliper.w0y = caliper.supports[indexFromBottom].edgeVector[1];
          caliper.w1x = -caliper.w0y;
          caliper.w1y = caliper.w0x;
        }
        // Replace minimum angle hull vertex index with the next one.
        caliper.supports[indexFromBottom].vertexIndex = next;
      }
    }

    // Update support vertex using the new bottom's edge vector for every support.
    for (let j = 0; j < 4; ++j) {
      const [caliperVx, caliperVy] = transformToCaliperCoords(
        caliper,
        caliper.supports[j].vertexIndex,
        polyline,
        convexHullVertexIndices,
      );
      caliper.supports[j].supportVertex[0] = caliperVx;
      caliper.supports[j].supportVertex[1] = caliperVy;
    }

    const { area, bottomLeftX, bottomLeftY, bottomRightX, bottomRightY, topLeftX, topLeftY } = calculateCaliperArea(
      caliper,
      polyline,
      convexHullVertexIndices,
    );
    if (area < minArea) {
      minArea = area;
      minAreaRect = {
        x: bottomLeftX,
        y: bottomLeftY,
        u: [bottomRightX - bottomLeftX, bottomRightY - bottomLeftY],
        v: [topLeftX - bottomLeftX, topLeftY - bottomLeftY],
      };
    }
  }

  return minAreaRect;
}

export function polyLine2FromBoundingRect(rect: BoundingRect): PolyLine2 {
  return polyLine2New([
    rect.x,
    rect.y,
    rect.x + rect.u[0],
    rect.y + rect.u[1],
    rect.x + rect.u[0] + rect.v[0],
    rect.y + rect.u[1] + rect.v[1],
    rect.x + rect.v[0],
    rect.y + rect.v[1],
    rect.x,
    rect.y,
  ]);
}
